import { getVersionUpgrade, VersionUpgrade } from '@uniswap/token-lists'
import { ChainTokenMap, tokensToChainTokenMap } from 'lib/hooks/useTokenList/utils'
import { useCallback, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import sortByListPriority from 'utils/listSort'

import { AppState } from '../index'
import { acceptListUpdate, fetchTokenList } from './actions'

export type TokenAddressMap = ChainTokenMap

type Mutable<T> = {
  -readonly [P in keyof T]: Mutable<T[P]>
}

export function useAllLists(): AppState['lists']['byUrl'] {
  return useAppSelector((state) => state.lists.byUrl)
}

// /**
//  * Combine the tokens in map2 with the tokens on map1, where tokens on map1 take precedence
//  * @param map1 the base token map
//  * @param map2 the map of additioanl tokens to add to the base map
//  */
export function combineMaps(map1: TokenAddressMap, map2: TokenAddressMap): TokenAddressMap {
  const chainIds = Object.keys(
    Object.keys(map1)
      .concat(Object.keys(map2))
      .reduce<{ [chainId: string]: true }>((memo, value) => {
        memo[value] = true
        return memo
      }, {})
  ).map((id) => parseInt(id))

  return chainIds.reduce<Mutable<TokenAddressMap>>((memo, chainId) => {
    memo[chainId] = {
      ...map2[chainId],
      // map1 takes precedence
      ...map1[chainId],
    }
    return memo
  }, {}) as TokenAddressMap
}

// // merge tokens contained within lists from urls
function useCombinedTokenMapFromUrls(urls: string[] | undefined): TokenAddressMap {
  const lists = useAllLists()
  return useMemo(() => {
    if (!urls) return {}
    return (
      urls
        .slice()
        // sort by priority so top priority goes last
        .sort(sortByListPriority)
        .reduce((allTokens, currentUrl) => {
          const current = lists[currentUrl]?.current
          if (!current) return allTokens
          try {
            return combineMaps(allTokens, tokensToChainTokenMap(current))
          } catch (error) {
            console.error('Could not show token list due to error', error)
            return allTokens
          }
        }, {})
    )
  }, [lists, urls])
}

// // filter out unsupported lists
export function useActiveListUrls(): string[] | undefined {
  const activeLists = useAllLists()
  const activeListUrls = Object.keys(activeLists)
  return activeListUrls
}

// // get all the tokens from active lists, combine with local default tokens
export function useCombinedTokenList(): TokenAddressMap {
  const activeListUrls = useActiveListUrls()
  const activeTokens = useCombinedTokenMapFromUrls(activeListUrls)
  return useMemo(() => activeTokens, [activeTokens])
}

/**
 * Hook to force refresh a specific token list
 * @returns Function to force refresh a specific list URL
 */
export function useForceRefreshTokenList() {
  const dispatch = useAppDispatch()
  const lists = useAllLists()

  return useCallback(
    async (listUrl: string) => {
      try {
        // Generate unique request id
        const requestId = `${listUrl}-${Date.now()}`
        dispatch(fetchTokenList.pending({ url: listUrl, requestId }))

        const response = await fetch(listUrl)
        const tokenList = await response.json()

        dispatch(
          fetchTokenList.fulfilled({
            url: listUrl,
            tokenList,
            requestId,
          })
        )
        // Force accept the update regardless of version
        const currentList = lists[listUrl]?.current
        if (currentList) {
          const bump = getVersionUpgrade(currentList.version, tokenList.version)
          if (bump !== VersionUpgrade.NONE) {
            dispatch(acceptListUpdate(listUrl))
          }
        }

        return true
      } catch (error) {
        console.error(`Failed to refresh token list ${listUrl}:`, error)
        dispatch(
          fetchTokenList.rejected({
            url: listUrl,
            requestId: `${listUrl}-${Date.now()}`,
            errorMessage: error.message,
          })
        )
        return false
      }
    },
    [dispatch, lists]
  )
}

/**
 * Hook to force refresh all active token lists
 * @returns Function to force refresh all active lists
 */
export function useForceRefreshAllTokenLists() {
  const activeListUrls = useActiveListUrls()
  const forceRefreshTokenList = useForceRefreshTokenList()
  return useCallback(async () => {
    if (!activeListUrls) return false
    try {
      // Clear local storage for lists if needed
      localStorage.removeItem('redux_localstorage_simple_lists')

      // Refresh all active lists
      const refreshPromises = activeListUrls.map((url) => forceRefreshTokenList(url))
      const results = await Promise.all(refreshPromises)

      // Return true if all refreshes succeeded
      const success = results.every(Boolean)
      if (success) {
        console.log('All token lists refreshed successfully')
      } else {
        console.error('Some token lists failed to refresh:', activeListUrls.filter((_, i) => !results[i]).join(', '))
      }
      return success
    } catch (error) {
      console.error('Failed to refresh all token lists:', error)
      return false
    }
  }, [activeListUrls, forceRefreshTokenList])
}
